import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
    useClassnames,
    Text,
    Dropdown,
    IconArrowsChevronBottom,
    IconMenu,
    IconCircleUser
} from '@wb-tech/ui-react';
import { shallowEqual } from 'react-redux';

import { useWindowScroll } from '#hook/use-window-scroll';
import { useSelector, useDispatch } from '#core/store';
import { defaultLocaleString } from '#core/const/locales';
import { formatNumber } from '#helper/format/number';
import { useSupplierFetch } from '#hook/use-supplier-fetch';
import { useRouteMatch } from '#hook/use-route-match';
import { useNavItems } from '#hook/use-nav-items';
import { analyticSDK } from '#analytics/wbsdk-analytic';
import { useConfigFetch } from '#hook/use-config-fetch';
import { useBalanceFetch } from '#hook/use-balance-fetch';
import { useHandleRequestError } from '#hook/use-handle-request-error';

import { getMediaQuery } from '#component/media-query/slice';
import { Skeleton } from '#component/ui/skeleton';
import { Slider } from '#component/slider';
import { sliderMenu, TOpenName } from '#component/slider/slice';
import { HeaderLogo } from '#component/header-logo';
import { IconCircleQuestion } from '#component/icons/circle-question';
import { IconWalletV2 } from '#component/icons/wallet';
import { CURRENCY } from '#route/finances/components/sum-text';
import { UserInfoContent } from '#component/header/components/user-info-content';
import { UserInfoContentV2 } from '#component/header/components/user-info-content/v2';

import { HeaderNavItem } from './components/nav-item';
import { TNavLink } from './types';

import style from './index.module.pcss';

export const Header = () => {
    const cn = useClassnames(style);
    const { i18n } = useTranslation();
    const { config } = useConfigFetch();
    const isUniversalAccount = Boolean(config?.unifiedAccountEnabled);
    const dispatch = useDispatch();

    const { match: { groups: matchGroups } } = useRouteMatch();
    const navItems = useNavItems();

    const isHeaderHidden = useWindowScroll();
    const { isMobile, isTablet, menuOpened } = useSelector((state) => ({
        isTablet  : state[getMediaQuery.name].isTablet,
        isMobile  : state[getMediaQuery.name].isMobile,
        menuOpened: state[sliderMenu.name].open
    }), shallowEqual);

    const { data: supplierData, cashback: supplierCashback, isSpecial, isLoading } = useSupplierFetch();
    const {
        data: balanceData,
        isLoading: isBalanceLoading,
        skip: balanceSkipped,
        error: balanceError
    } = useBalanceFetch();

    useHandleRequestError([balanceError]);

    const unifiedSum = formatNumber(balanceData?.content?.unifiedAccount || 0);
    const cashbackValue = isSpecial ? supplierCashback : balanceData?.content?.cashback;

    useEffect(() => {
        const setLanguage = async () => {
            const language = supplierData?.language ?? defaultLocaleString;

            await i18n.changeLanguage(language);
        };

        if(supplierData) {
            void setLanguage();
        }
    }, [i18n, supplierData]);

    const onClickOpenSliderMenu = useCallback((type: TOpenName) => () => {
        if(menuOpened && menuOpened === type) {
            dispatch(sliderMenu.actions.reset());
        } else {
            dispatch(sliderMenu.actions.set({ open: type }));
        }
    }, [dispatch, menuOpened]);

    const elUserInfoContent = useCallback(() => {
        return (
            isUniversalAccount ? (
                <UserInfoContentV2
                    supplierData={supplierData}
                    balanceData={balanceData}
                    cashbackValue={cashbackValue}
                />
            ) : (
                <UserInfoContent
                    supplierData={supplierData}
                    balanceSkipped={balanceSkipped}
                    balanceData={balanceData}
                />
            )
        );
    }, [isUniversalAccount, supplierData, balanceData, balanceSkipped, cashbackValue]);

    const onClickDropdown = useCallback(() => {
        void analyticSDK.profileT({});
    }, []);

    const elUserInfo = useMemo(() => {
        if(isMobile) {
            let content = <IconCircleUser svg={{ className: cn('header__mobile-user') }} />;

            if(isLoading || isBalanceLoading) {
                content = (
                    <Skeleton
                        presetShape="round"
                        presetWidth={24}
                    />
                );
            }

            return (
                <button
                    type="button"
                    className={cn('header__mobile-user-trigger')}
                    onClick={isLoading || isBalanceLoading ? undefined : onClickOpenSliderMenu('user-info')}
                >
                    {content}
                </button>
            );
        }

        if(isLoading || isBalanceLoading) {
            return (
                <Skeleton
                    presetWidth={150}
                    presetHeight={40}
                />
            );
        }

        if(supplierData) {
            return (
                <Dropdown
                    triggerOffset={8}
                    triggerElement={(
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                        <div
                            className={cn('header__dropdown-trigger')}
                            onClick={onClickDropdown}
                        >
                            <div className={cn('header__dropdown-trigger-text')}>
                                {supplierData.name}
                            </div>
                            <IconArrowsChevronBottom />
                        </div>
                    )}
                    render={elUserInfoContent}
                />
            );
        }

        return null;
    }, [
        isMobile,
        isLoading,
        isBalanceLoading,
        supplierData,
        cn,
        onClickOpenSliderMenu,
        elUserInfoContent,
        onClickDropdown
    ]);

    const elInfoBlock = useMemo(() => {
        return (
            <div className={cn('header__user-block')}>
                {/* TODO Нотификации пока что отключены */}
                {/* <Notification /> */}
                {/* {!isMobile && <div className={cn('header__divider')} />} */}
                {elUserInfo}
            </div>
        );
    }, [cn, elUserInfo]);

    const matchUrl = useMemo(() => {
        const match = matchGroups.campaigns ?? matchGroups.media ?? matchGroups.brandzone ?? matchGroups.myShop;

        return match?.pathname as TNavLink | undefined;
    }, [matchGroups]);

    const elNav = useMemo(() => {
        return (
            <nav className={cn('header__nav-container')}>
                {Object.values(navItems).map((item) => (
                    <HeaderNavItem
                        isMobile={isMobile || isTablet}
                        key={item.to}
                        item={item}
                        onClick={onClickOpenSliderMenu('menu')}
                    />
                ))}
            </nav>
        );
    }, [cn, navItems, isMobile, isTablet, onClickOpenSliderMenu]);

    const elTabs = useMemo(() => {
        const tabsElements = matchUrl ? navItems[matchUrl]?.options : [];

        return tabsElements?.map((tab) => (
            <NavLink
                key={tab.text}
                to={tab.to}
                className={({ isActive }) => {
                    return cn('header__nav-tab', {
                        'header__nav-tab_active': isActive
                    });
                }}
                onClick={() => tab?.analytics && tab.analytics()}
            >
                <Text presetColor="inherit">{tab.text}</Text>
                {tab.additionalContent}
            </NavLink>
        ));
    }, [matchUrl, navItems, cn]);

    const elMenu = useMemo(() => {
        if(isMobile || isTablet) {
            return (
                <button
                    type="button"
                    className={cn('header__mobile-menu-trigger')}
                    onClick={onClickOpenSliderMenu('menu')}
                >
                    <IconMenu svg={{ className: cn('header__mobile-menu-icon') }} />
                </button>
            );
        }

        return null;
    }, [cn, isMobile, isTablet, onClickOpenSliderMenu]);

    const elSliders = useMemo(() => {
        if(isMobile || isTablet) {
            return (
                <Fragment>
                    <Slider
                        name="menu"
                        direction="left"
                    >
                        {elNav}
                    </Slider>

                    <Slider
                        name="user-info"
                        direction="right"
                        contentPaddingPreset="disable"
                    >
                        {elUserInfoContent()}
                    </Slider>
                </Fragment>
            );
        }
    }, [isMobile, isTablet, elNav, elUserInfoContent]);

    return (
        <header
            className={cn('header', {
                'header_hidden'         : isHeaderHidden && !isMobile,
                'header_without-nav-bar': !matchUrl,
                'header_with-open-menu' : isMobile && menuOpened === 'menu'
            })}
        >
            <div className={cn('header__wrapper')}>
                {elMenu}

                <HeaderLogo />

                {!isMobile && !isTablet && elNav}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <NavLink
                        className={cn('header__dialogs-link')}
                        to="support/dialogs"
                        onClick={() => analyticSDK.supportMainV({ flow: 'direct' })}
                    >
                        <IconCircleQuestion />
                    </NavLink>

                    <div className={cn('header__divider')} />

                    {elInfoBlock}

                </div>

            </div>

            {!isMobile && !isTablet && matchUrl && (
                <nav className={cn('header__nav-tabs')}>
                    {elTabs}
                    {isUniversalAccount && (
                        <NavLink
                            className={cn('balance-icon')}
                            to="/campaigns/finances"
                        >
                            <IconWalletV2 className={cn('balance-wallet')} />
                            <span className={cn('balance-text')}>
                                {`${unifiedSum} `}
                                <span className={cn('balance-price')}>{CURRENCY.rub}</span>
                            </span>
                        </NavLink>
                    )}

                </nav>
            )}

            {elSliders}
        </header>
    );
};
